/* ============================================================================
   kw-cart.js — Kasuwa cart drawer (AJAX cart)
   ----------------------------------------------------------------------------
   This module owns the cart drawer once loaded. It intentionally supersedes
   the drawer block in assets/kw-theme.js WITHOUT modifying that file:
   drawer clicks and add-to-cart submits are handled here in the capture
   phase and stopPropagation() keeps the legacy handlers from double-firing.
   If this file fails to load, kw-theme.js keeps working as a fallback.

   Behavior:
   - Every quantity change or remove goes through /cart/change.js and the
     whole drawer (line items, line-level and cart-level discounts, subtotal,
     cart count) is re-rendered from the fresh JSON response, so discounts,
     selling plans, and unit prices always refresh with the line items.
   - Checkout submits the real cart form (button name="checkout",
     action routes.cart_url). No hardcoded /checkout URL.
   - Dependency-free, non-minified. Only talks to Shopify's own cart API.
   ========================================================================== */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cartdrawer]');
  var itemsWrap = document.querySelector('[data-cartdrawer-items]');
  var foot = document.querySelector('[data-cartdrawer-foot]');
  var scrim = document.querySelector('[data-scrim]');
  if (!drawer || !itemsWrap) return;

  /* Localized strings stashed on the drawer by the Liquid snippet, so no
     English is hardcoded here. */
  var STR = {
    per: drawer.getAttribute('data-kw-per') || 'per',
    decrease: drawer.getAttribute('data-kw-decrease') || 'Decrease quantity',
    increase: drawer.getAttribute('data-kw-increase') || 'Increase quantity',
    remove: drawer.getAttribute('data-kw-remove-label') || 'Remove'
  };

  var cachedCart = null;
  var emptyHTML = '<div class="kw-cartdrawer__empty"><p>Your cart is empty.</p></div>';
  var emptyNode = itemsWrap.querySelector('.kw-cartdrawer__empty');
  if (emptyNode) emptyHTML = emptyNode.outerHTML;

  /* ---------- helpers ---------- */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  }

  function thumb(url) {
    return String(url).replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1');
  }

  /* ---------- line item template (mirrors snippets/kw-cart-drawer.liquid) ---------- */
  function lineItemHTML(item, currency) {
    var img = item.image
      ? '<img src="' + esc(thumb(item.image)) + '" alt="" loading="lazy" width="64" height="64">'
      : '';

    var options = '';
    if (item.options_with_values && item.options_with_values.length &&
        item.variant_title !== 'Default Title') {
      options = item.options_with_values.map(function (opt) {
        return '<p class="kw-lineitem__variant">' + esc(opt.name) + ': ' + esc(opt.value) + '</p>';
      }).join('');
    }

    var sellingPlan = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlan = '<p class="kw-lineitem__variant">' +
        esc(item.selling_plan_allocation.selling_plan.name) + '</p>';
    }

    var unitPrice = '';
    var upm = item.unit_price_measurement;
    if (upm) {
      unitPrice = '<p class="kw-lineitem__variant">' +
        money(item.unit_price, currency) +
        '<span aria-hidden="true"> / </span>' +
        '<span class="kw-sr-only">' + esc(STR.per) + ' </span>' +
        (upm.reference_value !== 1 ? esc(upm.reference_value) : '') +
        esc(upm.reference_unit) +
        '</p>';
    }

    var discounts = '';
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      discounts = '<p class="kw-lineitem__discount">' +
        item.line_level_discount_allocations.map(function (da) {
          var title = da.discount_application ? da.discount_application.title : '';
          return esc(title) + ' (-' + money(da.amount, currency) + ')';
        }).join('<br>') +
        '</p>';
    }

    var price = '';
    if (item.original_line_price !== item.final_line_price) {
      price += '<s class="kw-lineitem__compare">' + money(item.original_line_price, currency) + '</s>';
    }
    price += money(item.final_line_price, currency);

    return '<div class="kw-lineitem" data-kw-key="' + esc(item.key) + '">' +
      img +
      '<div class="kw-lineitem__info">' +
        '<p class="kw-lineitem__title kw-lineitem__title--full"><a href="' + esc(item.url) + '">' +
          esc(item.product_title) + '</a></p>' +
        options + sellingPlan + unitPrice + discounts +
        '<span class="kw-lineitem__qty">' +
          '<button type="button" data-kw-qty="-1" data-kw-key="' + esc(item.key) + '" aria-label="' + esc(STR.decrease) + '">&minus;</button>' +
          '<span data-kw-qty-value>' + esc(item.quantity) + '</span>' +
          '<button type="button" data-kw-qty="1" data-kw-key="' + esc(item.key) + '" aria-label="' + esc(STR.increase) + '">+</button>' +
        '</span>' +
      '</div>' +
      '<div class="kw-lineitem__right">' +
        '<span class="kw-lineitem__price">' + price + '</span>' +
        '<a class="kw-cart-remove" href="' + esc(item.url_to_remove) + '" data-kw-remove data-kw-key="' + esc(item.key) + '">' + esc(STR.remove) + '</a>' +
      '</div>' +
    '</div>';
  }

  /* ---------- render ---------- */
  function renderCart(cart) {
    cachedCart = cart;
    var count = cart.item_count || 0;

    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].style.display = count > 0 ? 'flex' : 'none';
    }

    if (!cart.items || !cart.items.length) {
      itemsWrap.innerHTML = emptyHTML;
      if (foot) foot.style.display = 'none';
      return;
    }

    itemsWrap.innerHTML = cart.items.map(function (item) {
      return lineItemHTML(item, cart.currency);
    }).join('');

    if (foot) {
      foot.style.display = '';
      var sub = foot.querySelector('[data-cartdrawer-subtotal]');
      if (sub) sub.textContent = money(cart.total_price, cart.currency);
      var discList = foot.querySelector('[data-kw-cart-discounts]');
      if (discList) {
        discList.innerHTML = (cart.cart_level_discount_applications || []).map(function (d) {
          return '<li>' + esc(d.title) + ': -' + money(d.total_allocated_amount, cart.currency) + '</li>';
        }).join('');
      }
    }
  }

  function refreshCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); return cart; })
      .catch(function () { return cachedCart || { item_count: 0, items: [] }; });
  }

  function changeQty(key, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function () { /* keep current drawer state on failure */ });
  }

  /* ---------- open / close ---------- */
  function openCart() {
    refreshCart().then(function () {
      drawer.classList.add('open');
      if (scrim) scrim.classList.add('show');
    });
  }

  function closeCart() {
    drawer.classList.remove('open');
    if (scrim) scrim.classList.remove('show');
  }

  /* Backstop: if anything renders drawer rows without our data-kw-key marker
     (for example the legacy template from kw-theme.js's initial page-load
     refresh), repaint with our full template from the cached cart. */
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function () {
      if (itemsWrap.querySelector('.kw-lineitem:not([data-kw-key])')) {
        if (cachedCart) renderCart(cachedCart);
        else refreshCart();
      }
    });
    observer.observe(itemsWrap, { childList: true });
  }

  /* ---------- events (capture phase: preempt legacy drawer handlers) ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    var openTrigger = t.closest('[data-cart-open]');
    var inDrawer = t.closest('[data-cartdrawer]');
    if (!openTrigger && !inDrawer) return;

    var qtyBtn = t.closest('[data-kw-qty]');
    if (qtyBtn) {
      e.preventDefault();
      e.stopPropagation();
      var key = qtyBtn.getAttribute('data-kw-key');
      var row = qtyBtn.closest('[data-kw-key]');
      var qtyEl = row ? row.querySelector('[data-kw-qty-value]') : null;
      var current = qtyEl ? parseInt(qtyEl.textContent, 10) : 0;
      changeQty(key, Math.max(0, current + parseInt(qtyBtn.getAttribute('data-kw-qty'), 10)));
      return;
    }

    var removeLink = t.closest('[data-kw-remove]');
    if (removeLink) {
      e.preventDefault();
      e.stopPropagation();
      changeQty(removeLink.getAttribute('data-kw-key'), 0);
      return;
    }

    if (t.closest('[data-cart-close]')) {
      e.preventDefault();
      e.stopPropagation();
      closeCart();
      return;
    }

    if (openTrigger) {
      e.preventDefault();
      e.stopPropagation();
      openCart();
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('open')) closeCart();
  });

  /* Add-to-cart forms: same AJAX flow the theme already used, now rendering
     the full drawer template. Falls back to a native submit on error. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-add-form')) return;
    e.preventDefault();
    e.stopPropagation();

    var btn = form.querySelector('[data-add-to-cart]');
    var orig = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(refreshCart)
      .then(function () {
        drawer.classList.add('open');
        if (scrim) scrim.classList.add('show');
      })
      .catch(function () { form.submit(); })
      .then(function () {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
      });
  }, true);

  /* Cart page steppers: the legacy handler looks for input[name="quantity"],
     but the cart page uses name="updates[]", so wire the steppers here. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('[data-cartdrawer]')) return;
    var stepper = t.closest('[data-cart-qty-minus],[data-cart-qty-plus]');
    if (!stepper) return;
    var wrap = stepper.closest('.kw-qty');
    var input = wrap ? wrap.querySelector('input') : null;
    if (!input) return;
    var v = parseInt(input.value, 10) || 0;
    v += stepper.hasAttribute('data-cart-qty-plus') ? 1 : -1;
    input.value = Math.max(0, v);
  });

  /* Initial paint: fetch the cart so the drawer uses the full template and
     the header badge count is correct even when the snippet rendered empty. */
  refreshCart();
})();
